/**
 * PokerHappyHourPanel — one-click Rake-Free Happy Hour toggles.
 *
 * Runs alongside the other Settings panels. Admins pick a duration (30 / 60
 * / 90 / 120 min) and either apply globally or to a single stake level. The
 * banner then flips on for every seated + spectating player in real time via
 * the poker WebSocket.
 */
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Zap, X, Clock } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const PRESETS = [30, 60, 90, 120];

export default function PokerHappyHourPanel() {
    const [tables, setTables] = useState([]);
    const [scope, setScope] = useState('all');
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        try {
            const { data } = await axios.get(`${API}/poker-room/tables`, { withCredentials: true });
            setTables(data.tables || []);
        } catch { /* silent */ }
    }, []);

    useEffect(() => { load(); const t = setInterval(load, 10_000); return () => clearInterval(t); }, [load]);

    const apply = async (minutes) => {
        setLoading(true);
        try {
            const res = await axios.post(`${API}/poker-room/admin/happy-hour`,
                { minutes, scope }, { withCredentials: true });
            if (minutes > 0) toast.success(`🎉 Rake-Free Happy Hour ON for ${minutes} min · ${res.data.tables_affected.length} table(s)`);
            else toast.message('Happy Hour cancelled');
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to update');
        } finally { setLoading(false); }
    };

    return (
        <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-[#0f0f14] to-[#050508] p-4 md:p-5" data-testid="poker-happy-hour-panel">
            <div className="flex items-center gap-2 mb-2">
                <Zap className="w-5 h-5 text-yellow-400" />
                <h3 className="text-lg font-bold text-white">Poker · Rake-Free Happy Hour</h3>
            </div>
            <p className="text-xs text-white/50 mb-4">
                Flip rake off for a promo window. Every player at the affected table(s) sees a live &quot;Rake-Free Hour&quot; banner; every pot pays out at 100% until the timer runs out.
            </p>

            {/* Scope */}
            <div className="mb-3">
                <label className="block text-[10px] uppercase tracking-wider text-white/50 mb-1">Apply to</label>
                <select value={scope} onChange={(e) => setScope(e.target.value)}
                        className="w-full bg-black/60 border border-white/20 rounded-lg px-3 py-2 text-white text-sm"
                        data-testid="hh-scope">
                    <option value="all">All Tables</option>
                    {tables.map((t) => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                </select>
            </div>

            {/* Preset durations */}
            <div className="flex flex-wrap gap-2 mb-3">
                {PRESETS.map((m) => (
                    <button key={m} onClick={() => apply(m)} disabled={loading}
                            className="flex items-center gap-1 px-4 py-2 rounded-lg bg-yellow-500 hover:bg-yellow-400 text-black font-black text-sm disabled:opacity-40"
                            data-testid={`hh-preset-${m}`}>
                        <Clock className="w-3.5 h-3.5" /> {m} min
                    </button>
                ))}
                <button onClick={() => apply(0)} disabled={loading}
                        className="flex items-center gap-1 px-4 py-2 rounded-lg bg-black/60 border border-red-500/40 text-red-300 hover:bg-red-500/10 text-sm"
                        data-testid="hh-cancel">
                    <X className="w-3.5 h-3.5" /> Cancel Active
                </button>
            </div>

            {/* Live status per table */}
            <div className="rounded-lg border border-white/10 bg-black/30 p-3">
                <div className="text-[10px] uppercase tracking-wider text-white/50 mb-2">Current status</div>
                {tables.map((t) => {
                    // The table list endpoint doesn't include rake_free_active — we hint via bootstrap.
                    // Show table name + stakes for now; full status appears on the table page itself.
                    return (
                        <div key={t.id} className="flex items-center justify-between py-1 text-xs">
                            <span className="text-white">{t.name}</span>
                            <span className="text-white/40">
                                Seated {t.seated}/{t.max_seats} · Hands {t.hands_played}
                            </span>
                        </div>
                    );
                })}
                {tables.length === 0 && <div className="text-xs text-white/40">Loading…</div>}
            </div>
        </div>
    );
}
